import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";
import { CardBackButton, PhoneField, isValidPhone, toE164 } from "/imports/ui/components/shared";
import api from "/imports/api/client";
import { POST_AUTH_REDIRECT_KEY } from "/imports/ui/views/JoinBookingView/useJoinBooking";

// The stored hash is taken over the hyphenated code, so "NRGC7NMKY" has to become
// "NRG-C7N-MKY" before it reaches the server or a correct code is rejected.
const formatAccessCode = (value) => {
  const raw = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 9);
  return raw.match(/.{1,3}/g)?.join("-") || "";
};

// Where a reminder-driven claim ends up: the booking the reminder was about.
const REMINDER_DESTINATION = "dashboard?tab=bookings&scope=upcoming";

function ClaimView() {
  const navigate = useNavigate();
  const { slug } = useWidget();
  const { claim, error: authError, clearError } = useAuth();
  const { filters } = useUrlFilters({});

  // A code carried in the link came from a booking reminder, so this client is
  // here to see that booking — land them on the Bookings tab once claimed. A
  // code typed in by hand (the one staff share directly) keeps the plain flow.
  const linkedCode = formatAccessCode(filters.code);
  const redirectTo = filters.redirect || (linkedCode ? REMINDER_DESTINATION : "");

  const [step, setStep] = useState("code");
  const [code, setCode] = useState(linkedCode);
  const [prefill, setPrefill] = useState(null);
  const [formData, setFormData] = useState({ email: "", phoneNumber: "", password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const loginTo = `/${slug}/login${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`;

  const handleBack = () => {
    if (step === "details") {
      setStep("code");
      return;
    }
    navigate(`/${slug}/signup${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`);
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const runVerify = async (value) => {
    setError(null);
    setErrorCode(null);
    clearError();

    const accessCode = formatAccessCode(value);
    if (!accessCode) {
      setError("Please enter your access code");
      return;
    }

    setLoading(true);
    try {
      const data = await api.claimVerify(accessCode, slug);
      setPrefill(data.client);
      setFormData((prev) => ({
        ...prev,
        email: data.client.email || "",
        phoneNumber: data.client.phoneNumber || ""
      }));
      setStep("details");
    } catch (err) {
      setError(err.message || "We couldn't verify this code. Please try again.");
      setErrorCode(err.code || null);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    runVerify(code);
  };

  // One tap from the reminder: the code is already in the link, so verify it on
  // arrival and open on the details step instead of asking them to retype it.
  const autoVerified = useRef(false);
  useEffect(() => {
    if (autoVerified.current || !linkedCode) return;
    autoVerified.current = true;
    runVerify(linkedCode);
  }, [linkedCode]);

  const handleClaim = async (e) => {
    e.preventDefault();
    setError(null);
    setErrorCode(null);
    clearError();

    if (!formData.email.trim()) {
      setError("Please enter your email");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    if (!formData.phoneNumber.trim()) {
      setError("Please enter your phone number");
      return;
    }
    // The prefill is only a convenience — the club's record may hold a number
    // typed at the desk years ago, and a half-typed one silently costs the client
    // every WhatsApp reminder. What the client confirms here is authoritative, so
    // it is held to the same format whether they retyped it or accepted ours.
    if (!isValidPhone(formData.phoneNumber)) {
      setError("Please enter a valid phone number");
      return;
    }
    if (!formData.password) {
      setError("Please choose a password");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const result = await claim({
        code: formatAccessCode(code),
        slug,
        email: formData.email.trim(),
        phoneNumber: toE164(formData.phoneNumber),
        password: formData.password
      });

      // A claimed account is new, so it must verify its email before signing in.
      // Collect preferences first, then route on (to login while unverified).
      const redirect = redirectTo;
      const claimedEmail = result?.email || formData.email.trim();
      // The verification email is often opened in another tab, which loses the
      // in-app state — VerifyEmailView reads this back and resumes the journey.
      if (linkedCode && redirect) localStorage.setItem(POST_AUTH_REDIRECT_KEY, `/${slug}/${redirect}`);
      if (result?.preferencesToken) {
        navigate(`/${slug}/preferences`, {
          state: { preferencesToken: result.preferencesToken, afterSave: result?.needsVerification ? "login" : "home", redirect, email: claimedEmail }
        });
      } else if (result?.needsVerification) {
        navigate(`/${slug}/check-email`, { state: { email: claimedEmail, redirect } });
      } else {
        navigate(`/${slug}/${redirect}`);
      }
    } catch (err) {
      setError(err.message || "We couldn't complete your signup. Please try again.");
      setErrorCode(err.code || null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={4}>
      <Card sx={{ maxWidth: "31.25rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <CardBackButton onClick={handleBack} />
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Claim Your Account
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            {step === "code" ? "Enter the access code your club shared with you." : "Confirm your details and set a password to finish."}
          </Typography>

          {(error || authError) &&
            (errorCode === "ALREADY_CLAIMED" || errorCode === "USER_EXISTS" ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {error || authError}
                <Box mt={1} display="flex" justifyContent="center">
                  <Button color="info" variant="outlined" size="small" component={RouterLink} to={loginTo}>
                    Sign in
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || authError}
              </Alert>
            ))}

          {step === "code" ? (
            <form onSubmit={handleVerify}>
              <TextField
                fullWidth
                label="Access Code"
                value={code}
                onChange={(e) => {
                  setCode(formatAccessCode(e.target.value));
                  setError(null);
                  setErrorCode(null);
                }}
                placeholder="XXX-XXX-XXX"
                autoFocus
                inputProps={{ style: { letterSpacing: 2, fontFamily: "monospace" } }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || errorCode === "ALREADY_CLAIMED" || errorCode === "USER_EXISTS"}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? "Checking..." : "Continue"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleClaim}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" value={prefill?.firstName || ""} disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" value={prefill?.lastName || ""} disabled />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange("email")}
                    required
                    autoComplete="email"
                    helperText={prefill?.emailRequired ? "We need your email to create your account" : "Update this if it's out of date or incorrect"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PhoneField
                    value={formData.phoneNumber}
                    onChange={(value) => setFormData((prev) => ({ ...prev, phoneNumber: value }))}
                    required
                    helperText="Update this if it's out of date or incorrect"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange("password")}
                    required
                    autoComplete="new-password"
                    helperText="At least 6 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((s) => !s)}
                            onMouseDown={(e) => e.preventDefault()}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange("confirmPassword")}
                    required
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword((s) => !s)}
                            onMouseDown={(e) => e.preventDefault()}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mt: 3, mb: 2 }}>
                {loading ? "Creating account..." : "Create Account"}
              </Button>
              <Button fullWidth onClick={() => setStep("code")} disabled={loading}>
                Use a different code
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ClaimView;
